import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import { auth } from "../../../config/firebaseConfig";
import { saveProject, loadProjectData, getPayloadByteSize } from "../services/projectService";

/**
 * Hook to manage project lifecycle: cloud persistence, dirty state detection,
 * new project reset, language switching, and keyboard save shortcuts.
 */
export const useWorkspaceProject = ({
  selectedChild,
  activeWorkspaceEnv,
  setActiveWorkspaceEnv,
  openDialog,
  onClearDraft,
}) => {
  const [projectName, setProjectName] = useState("Unnamed Project");
  const [projectId, setProjectId] = useState(null);
  const [loadedProjectData, setLoadedProjectData] = useState(null);
  const [activeWorkspaceCode, setActiveWorkspaceCode] = useState(null);
  const [activeWorkspaceFiles, setActiveWorkspaceFiles] = useState(null);
  const [lastSavedCloudCode, setLastSavedCloudCode] = useState(null);
  const [baselineCode, setBaselineCode] = useState(null);
  const [baselineFiles, setBaselineFiles] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [workspaceKey, setWorkspaceKey] = useState(0);
  const [projectSizeBytes, setProjectSizeBytes] = useState(0);

  const isScratchOrJunior =
    activeWorkspaceEnv === "scratch" ||
    activeWorkspaceEnv === "scratch_jr" ||
    activeWorkspaceEnv === "junior" ||
    activeWorkspaceEnv === "junior_blocks_basic" ||
    activeWorkspaceEnv === "junior_blocks_adv";

  useEffect(() => {
    let size = 0;
    if (activeWorkspaceCode && typeof activeWorkspaceCode === "object" && typeof activeWorkspaceCode.projectSizeBytes === "number") {
      size = activeWorkspaceCode.projectSizeBytes;
    } else {
      size = getPayloadByteSize(activeWorkspaceCode, activeWorkspaceFiles, isScratchOrJunior);
    }
    setProjectSizeBytes(size);
  }, [activeWorkspaceCode, activeWorkspaceFiles, isScratchOrJunior]);

  // Normalize code for comparison (handles JSON objects vs code strings and multi-file structures)
  const isDirty = useMemo(() => {
    if (activeWorkspaceFiles || baselineFiles) {
      const normalizeFiles = (f) => {
        if (!f || !Array.isArray(f)) return "";
        return JSON.stringify(
          f.map((file) => ({
            name: file.name,
            path: file.path,
            content: file.content,
            isEntry: !!file.isEntry,
            parentId: file.parentId,
            type: file.type,
          }))
        );
      };
      if (normalizeFiles(activeWorkspaceFiles) !== normalizeFiles(baselineFiles)) {
        return true;
      }
    }
    if (!activeWorkspaceCode && !baselineCode) return false;
    const normalize = (c) => {
      if (!c) return "";
      if (typeof c === "object") {
        if (c.projectJson) return JSON.stringify(c.projectJson);
        return JSON.stringify(c);
      }
      return String(c);
    };
    return normalize(activeWorkspaceCode) !== normalize(baselineCode);
  }, [activeWorkspaceCode, baselineCode, activeWorkspaceFiles, baselineFiles]);

  const handleWorkspaceCodeChange = useCallback((code, env, files) => {
    if (code !== undefined && code !== null) {
      setActiveWorkspaceCode(code);
      setBaselineCode((prev) => {
        if (prev === null) return code;
        return prev;
      });
    }
    if (files !== undefined) {
      setActiveWorkspaceFiles(files);
      setBaselineFiles((prev) => {
        if (prev === null) return files;
        return prev;
      });
    }
    if (env && setActiveWorkspaceEnv) {
      setActiveWorkspaceEnv(env);
    }
  }, [setActiveWorkspaceEnv]);

  const executeNewProject = useCallback(() => {
    setProjectName("Unnamed Project");
    setProjectId(null);
    setLoadedProjectData(null);
    setActiveWorkspaceCode(null);
    setActiveWorkspaceFiles(null);
    setLastSavedCloudCode(null);
    setBaselineCode(null);
    setBaselineFiles(null);
    setProjectSizeBytes(0);

    if (onClearDraft) {
      onClearDraft();
    }

    // Force remount of modular workspace editors & iframes
    setWorkspaceKey((prev) => prev + 1);
  }, [onClearDraft]);

  const handleNewProjectClick = useCallback(() => {
    if (isDirty) {
      openDialog({
        type: "NEW_PROJECT",
        title: "Start New Project?",
        message:
          "You have unsaved changes. Starting a new project will clear your current work. Are you sure you want to proceed?",
        confirmText: "Start New",
        confirmClass: "bg-purple-600 hover:bg-purple-500 text-white",
        cancelText: "Cancel",
        showInput: false,
        inputValue: "",
        payload: null,
      });
    } else {
      executeNewProject();
    }
  }, [isDirty, openDialog, executeNewProject]);

  const executeSaveToCloud = useCallback(
    async (finalName) => {
      if (!selectedChild || (!activeWorkspaceCode && !activeWorkspaceFiles)) {
        toast.warn("No code or workspace active to save.");
        return;
      }

      setIsSaving(true);
      try {
        const parentId =
          selectedChild.parentUid || auth?.currentUser?.uid;
        const childId = selectedChild.id || selectedChild.uid;

        const isJuniorEnv =
          activeWorkspaceEnv === "scratch_jr" ||
          activeWorkspaceEnv === "junior" ||
          activeWorkspaceEnv === "junior_blocks_basic" ||
          activeWorkspaceEnv === "junior_blocks_adv";

        let envToSave = activeWorkspaceEnv;
        if (isJuniorEnv) {
          const currentSubMode =
            activeWorkspaceEnv === "junior_blocks_adv"
              ? "advanced"
              : activeWorkspaceEnv === "junior_blocks_basic"
              ? "basic"
              : selectedChild?.juniorSubMode ||
                (selectedChild?.grade === "Class 1" ? "basic" : "advanced");

          envToSave =
            currentSubMode === "advanced"
              ? "junior_blocks_adv"
              : "junior_blocks_basic";
        }

        const savedProj = await saveProject({
          projectId, // null for new projects, string for updates
          childId,
          parentUid: parentId,
          projectName: finalName || projectName,
          environment: envToSave,
          code: activeWorkspaceCode,
          files: activeWorkspaceFiles || undefined,
        });

        setProjectId(savedProj.id);
        setProjectName(finalName || projectName);
        setLastSavedCloudCode(activeWorkspaceCode);
        setBaselineCode(activeWorkspaceCode); // Reset dirty state
        setBaselineFiles(activeWorkspaceFiles);
        if (onClearDraft) {
          onClearDraft();
        }
        toast.success("Project saved successfully!");
      } catch (error) {
        console.error("Failed to save project:", error);
        if (error && error.code === "PROJECT_SIZE_EXCEEDED") {
          toast.warning(error.message, { autoClose: 6000 });
          if (openDialog) {
            openDialog({
              type: "PROJECT_SIZE_LIMIT",
              title: "Project Exceeds Cloud Limit (100 KB)",
              message: `Your project is currently ${(error.actualSize / 1024).toFixed(1)} KB. Cloud sync has a limit of 100 KB per project. Would you like to download this project to your computer so you don't lose any of your work?`,
              confirmText: "Download to Computer",
              confirmClass: "bg-purple-600 hover:bg-purple-500 text-white",
              cancelText: "Keep Editing",
              showInput: false,
            });
          }
        } else {
          toast.error(error?.message || "Failed to save project.");
        }
      } finally {
        setIsSaving(false);
      }
    },
    [
      selectedChild,
      activeWorkspaceCode,
      activeWorkspaceFiles,
      projectId,
      projectName,
      activeWorkspaceEnv,
    ]
  );

  const handleSaveClick = useCallback(() => {
    if (
      projectName.trim().toLowerCase() === "unnamed project" ||
      projectName.trim() === ""
    ) {
      openDialog({
        type: "RENAME",
        title: "Name Your Project",
        message: "",
        confirmText: "Save Project",
        confirmClass: "bg-purple-600 hover:bg-purple-500 text-white",
        cancelText: "Cancel",
        showInput: true,
        inputValue: projectName === "Unnamed Project" ? "" : projectName,
        payload: "save",
      });
    } else {
      executeSaveToCloud(projectName);
    }
  }, [projectName, openDialog, executeSaveToCloud]);

  // Keep a mutable ref of handleSaveClick to avoid stale closures in listeners
  const handleSaveClickRef = useRef(handleSaveClick);
  useEffect(() => {
    handleSaveClickRef.current = handleSaveClick;
  });

  // Global keyboard shortcut (Ctrl+S / Cmd+S) and iframe postMessage listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        e.stopPropagation();
        if (handleSaveClickRef.current) {
          handleSaveClickRef.current();
        }
      }
    };

    const handleWindowMessage = (e) => {
      if (e.data && e.data.type === "TRIGGER_CLOUD_SAVE") {
        if (handleSaveClickRef.current) {
          handleSaveClickRef.current();
        }
      }
    };

    const handleCustomSaveEvent = () => {
      if (handleSaveClickRef.current) {
        handleSaveClickRef.current();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("message", handleWindowMessage);
    window.addEventListener("workspace-save-shortcut", handleCustomSaveEvent);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("message", handleWindowMessage);
      window.removeEventListener("workspace-save-shortcut", handleCustomSaveEvent);
    };
  }, []);

  const executeLanguageSwitch = useCallback(
    (newLang) => {
      if (setActiveWorkspaceEnv) {
        setBaselineCode(null);
        setActiveWorkspaceEnv(newLang);
      }
    },
    [setActiveWorkspaceEnv]
  );

  const handleRequestLanguageChange = useCallback(
    (newLang) => {
      executeLanguageSwitch(newLang);
    },
    [executeLanguageSwitch]
  );

  const executeLoadProjectFromCloud = useCallback(
    async (project) => {
      if (!project) return;
      const projectEnv =
        project.environment ||
        (project.language === "python"
          ? "python"
          : project.language === "javascript"
          ? "javascript"
          : "scratch");

      const isJuniorEnv =
        projectEnv === "scratch_jr" ||
        projectEnv === "junior" ||
        projectEnv === "junior_blocks_basic" ||
        projectEnv === "junior_blocks_adv";

      const isBlockEnv = projectEnv === "scratch" || isJuniorEnv;

      if (projectEnv && projectEnv !== activeWorkspaceEnv && setActiveWorkspaceEnv) {
        setActiveWorkspaceEnv(projectEnv);
      }

      setProjectName(project.projectName || "Unnamed Project");
      setProjectId(project.id);

      try {
        const loaded = await loadProjectData(project);

        if (isBlockEnv) {
          const blockPayload = loaded?.buffer || loaded?.code || project.code;
          setLoadedProjectData(blockPayload);
          setActiveWorkspaceCode(blockPayload);
          setLastSavedCloudCode(blockPayload);
          setBaselineCode(blockPayload);
          setBaselineFiles(null);
          setActiveWorkspaceFiles(null);
        } else if (Array.isArray(loaded?.files) && loaded.files.length > 0) {
          const normalizedFiles = loaded.files.map((f, idx) => ({
            id: f.id || `file_${idx}`,
            name: f.name,
            type: f.type || "file",
            parentId: f.parentId || null,
            path: f.path || f.name,
            content: f.content ?? "",
            isEntry: !!f.isEntry,
          }));
          setActiveWorkspaceFiles(normalizedFiles);

          const entryFile =
            normalizedFiles.find((f) => f.isEntry) || normalizedFiles[0];
          const entryContent = loaded.code || (entryFile ? entryFile.content : "");

          setLoadedProjectData({ code: entryContent, files: normalizedFiles });
          setActiveWorkspaceCode(entryContent);
          setLastSavedCloudCode(entryContent);
          setBaselineCode(entryContent);
          setBaselineFiles(normalizedFiles);
        } else {
          setActiveWorkspaceFiles(null);
          const projectCode = loaded?.code ?? project.code ?? "";
          setLoadedProjectData(projectCode);
          setActiveWorkspaceCode(projectCode);
          setLastSavedCloudCode(projectCode);
          setBaselineCode(projectCode);
          setBaselineFiles(null);
        }

        if (onClearDraft) {
          onClearDraft();
        }

        setWorkspaceKey((prev) => prev + 1);
        toast.success(`Opened "${project.projectName || "Project"}"`);
      } catch (err) {
        console.error("Failed to load project from storage:", err);
        toast.error(err.message || "Failed to load project from storage.");
      }
    },
    [activeWorkspaceEnv, setActiveWorkspaceEnv, onClearDraft]
  );

  const handleLoadProjectFromCloud = useCallback(
    (project) => {
      if (!project) return;

      const projectEnv =
        project.environment ||
        (project.language === "python"
          ? "python"
          : project.language === "javascript"
          ? "javascript"
          : "scratch");

      const isCurrentJunior =
        activeWorkspaceEnv === "scratch_jr" ||
        activeWorkspaceEnv === "junior" ||
        activeWorkspaceEnv === "junior_blocks_basic" ||
        activeWorkspaceEnv === "junior_blocks_adv";

      const isTargetJunior =
        projectEnv === "scratch_jr" ||
        projectEnv === "junior" ||
        projectEnv === "junior_blocks_basic" ||
        projectEnv === "junior_blocks_adv";

      const isSameEnv =
        projectEnv === activeWorkspaceEnv || (isCurrentJunior && isTargetJunior);

      const getEnvDisplayName = (env) => {
        if (env === "python") return "Python";
        if (env === "javascript") return "JavaScript";
        if (env === "scratch") return "Scratch Visual Blocks";
        if (env === "junior_blocks_basic") return "Junior Blocks (Basic)";
        if (env === "junior_blocks_adv") return "Junior Blocks (Advanced)";
        if (env === "scratch_jr" || env === "junior") return "Junior Blocks";
        return env ? env.toUpperCase() : "Workspace";
      };

      const currentEnvName = getEnvDisplayName(activeWorkspaceEnv);
      const targetEnvName = getEnvDisplayName(projectEnv);
      const projName = project.projectName || "Unnamed Project";

      if (isSameEnv) {
        openDialog({
          type: "LOAD_PROJECT",
          title: `Open "${projName}"?`,
          message:
            "Opening this project will replace your current workspace content. Any unsaved changes in your active editor will be cleared. Are you sure you want to proceed?",
          confirmText: "Open Project",
          confirmClass: "bg-purple-600 hover:bg-purple-500 text-white",
          cancelText: "Cancel",
          showInput: false,
          payload: project,
        });
      } else {
        openDialog({
          type: "LOAD_PROJECT",
          title: `Switch to ${targetEnvName} & Open Project?`,
          message: `You selected a ${targetEnvName} project ("${projName}"). Opening it will switch your editor from ${currentEnvName} to ${targetEnvName} environment and clear your current work. Any unsaved changes will be lost. Are you sure you want to proceed?`,
          confirmText: `Switch to ${targetEnvName} & Open`,
          confirmClass: "bg-purple-600 hover:bg-purple-500 text-white",
          cancelText: "Cancel",
          showInput: false,
          payload: project,
        });
      }
    },
    [activeWorkspaceEnv, openDialog]
  );

  return {
    projectName,
    setProjectName,
    projectId,
    setProjectId,
    loadedProjectData,
    setLoadedProjectData,
    activeWorkspaceCode,
    setActiveWorkspaceCode,
    activeWorkspaceFiles,
    setActiveWorkspaceFiles,
    lastSavedCloudCode,
    setLastSavedCloudCode,
    baselineCode,
    setBaselineCode,
    isSaving,
    workspaceKey,
    setWorkspaceKey,
    isDirty,
    projectSizeBytes,
    handleWorkspaceCodeChange,
    executeNewProject,
    handleNewProjectClick,
    executeSaveToCloud,
    handleSaveClick,
    handleRequestLanguageChange,
    executeLanguageSwitch,
    executeLoadProjectFromCloud,
    handleLoadProjectFromCloud,
  };
};

export default useWorkspaceProject;
